import { redirect } from "next/navigation";

import { getAuthCookie, setCookie } from "@/services/CookiesService";
import { isSubscriptionCancelled, userProfileApiToDataTransformer } from "@/services/UserService";

import { patchSubscriptionPayload } from "@/interface/api/patchSubscription/types";
import patchSubscriptionHandler from "@/interface/api/patchSubscription";
import postSubscriptionHandler from "@/interface/api/postSubscription";

import getProfileHandler from "@api/getProfile";
import { paths } from "@/utils/paths";

import { CANCELLED_SUBSCRIPTION } from "@/containers/SubscriptionPlansContainer/types";
import { SubscriptionStatus } from "@/types/subscription";
import { SelectPlanActionPayload } from "./types";

const fetchTransformedUser = async (authorization: string) => {
  const response = await getProfileHandler.getProfile({ authorization });
  return userProfileApiToDataTransformer(response?.data);
};

const syncUserContextWithLatestProfile = async (
  authorization: string,
  setUserContext?: (userData) => void
) => {
  if (!setUserContext) return;

  try {
    const updatedUser = await fetchTransformedUser(authorization);
    setUserContext(updatedUser);
  } catch {
    // Best-effort sync: do not block plan assignment if profile refresh fails.
  }
};

const getProfile = async (payload: SelectPlanActionPayload, authorization) => {
  const user = payload?.user;
  if (user) {
    return user.subscription?.status === SubscriptionStatus.ACTIVE
      ? redirect(paths.myQrCodes())
      : await getProfileHandler.getProfile({ authorization });
  }
};

const renewSubscription = async (
  payload: SelectPlanActionPayload,
  setUserContext?: (userData) => void,
  xForwardedFor?: string
) => {
  const requestPostSubscription = postSubscriptionHandler.postSubscription;
  const authorization = getAuthCookie();

  try {
    await requestPostSubscription(authorization);
    const res = await getProfile({ ...payload, planId: payload.planId }, authorization);
    const user = userProfileApiToDataTransformer(res?.data);

    const requestData: patchSubscriptionPayload = {
      userSubscriptionPlanId: user.subscription?.id,
      subscriptionId: payload.planId,
      authorization,
      xForwardedFor,
    };

    const response = await patchSubscriptionHandler.patchSubscription(requestData);

    if (response.status !== 204 && response.status !== 200) {
      throw new Error("Error renewing subscription");
    }

    await syncUserContextWithLatestProfile(authorization, setUserContext);
  } catch (error) {
    console.error("Error renewing subscription", error);
    throw error;
  }
};

export const assignPlanToUser = async (
  payload: SelectPlanActionPayload,
  setUserContext: (userData) => void,
  xForwardedFor?: string
): Promise<void> => {
  const { planId } = payload;
  const authorization = getAuthCookie();
  try {
    const user = await fetchTransformedUser(authorization);

    if (!user) {
      throw new Error("User not found");
    }

    const requestData: patchSubscriptionPayload = {
      userSubscriptionPlanId: user.subscription?.id,
      subscriptionId: planId,
      authorization,
      xForwardedFor,
    };

    if (isSubscriptionCancelled(user)) {
      setCookie(CANCELLED_SUBSCRIPTION, true);
      return renewSubscription(payload, setUserContext, xForwardedFor);
    }

    const response = await patchSubscriptionHandler.patchSubscription(requestData);

    if (response.status !== 204 && response.status !== 200) {
      throw new Error("Error assigning plan to user");
    }

    await syncUserContextWithLatestProfile(authorization, setUserContext);
  } catch (error) {
    console.error("Error assigning plan to user", error);
    throw error;
  }
};
